<style lang="less">
.renewal-buttons {
  position: relative;

  .renewal-buttons-status-text {
    text-transform: uppercase;
    font-size: 9pt;
    margin-top: 10px;
    opacity: 0.6;
  }

  .renewal-buttons-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .btn-renew {
    background-color: #FFF;
    border-radius: 10px;
    padding: 7px 10px;
    text-align: center;
    font-weight: bold;
    cursor: pointer;
    transition: all 150ms ease-in-out;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    position: relative;
    z-index: 1;
    margin-top: 8px;

    > .btn-renew-title {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    > .btn-renew-subtitle {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7em;
      opacity: 0.5;
    }

    > .mdi-icon {
      border-left: dashed 1px #06f;
      flex: 0 0 min-content;
    }

    &:hover {
      transform: scale(1.05);
    }
  }
}
</style>

<template>
  <div class="renewal-buttons">
    <div v-if="isLoading" class="renewal-buttons-loading">
      <mdi-icon icon="progress_activity" spin />
    </div>
    <div :style="{ visibility: isLoading ? 'hidden' : 'visible' }">
      <template v-if="isPromo">
        <div v-for="btn of btns" style="text-decoration: line-through;">
          <strong>{{ btn.title }}</strong> {{ btn.subtitle }}
        </div>
        <div class="btn-renew theme-color" @click="clickPromo" style="z-index: 3;">
          <div class="btn-renew-title">PROMO</div>
          <div class="btn-renew-subtitle">Free for 6 months!</div>
          <mdi-icon icon="chevron_right" style="margin-left: 5px; font-size: 1.6em;" />
        </div>
      </template>
      <template v-else>
        <div style="text-transform: uppercase; font-size: 9pt; margin-top: 10px; opacity: 0.9; padding: 0 10px;">
          A Weather Nerd subscription provides access to all features including
          forecasts, radar, and more:
        </div>
        <div class="btn-renew theme-color" @click="btn.click" v-for="btn of btns" style="z-index: 3;">
          <div class="btn-renew-title">{{ btn.title }}</div>
          <div class="btn-renew-subtitle">{{ btn.subtitle }}</div>
          <mdi-icon icon="chevron_right" style="margin-left: 5px; font-size: 1.6em;" />
        </div>
      </template>
      <div class="btn-renew theme-color" @click="clickRefresh" style="margin-top:20px; z-index: 1;">
        <div class="btn-renew-title">RESTORE PURCHASES</div>
        <mdi-icon icon="chevron_right" style="margin-left: 5px; font-size: 1.6em;" />
      </div>
      <div class="renewal-buttons-status-text">
        <div v-if="statusText">{{ statusText }} • </div>
        <span @click="clickTermsOfUse">Terms of Use</span>
        •
        <span @click="clickPrivacyPolicy">Privacy Policy</span>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref } from 'vue';
import MdiIcon from '@/client/mdi-icon.vue';
import { nativeStoreKitService } from '@/native/native-store-kit-service';
import { nativeService } from '@/native/service';
import { clientApi } from '@/client/api';
import { clientContext } from '@/client/context';
import { setTimeoutAsync } from '@/utils/timeout';
import { nativeReviewPromptService } from '@/native/review-prompt-service';
import { isPromo } from '@/registration/is-promo';

const emits = defineEmits(['done']);
const btns = ref<{ title:string, subtitle: string, click: Function }[]>(null);
const isPurchasing = ref(false);
const isLoading = computed(() => !btns.value || isPurchasing.value);
const receiptStatusRef:Ref<IReceiptStatus> = ref(clientContext.report?.receiptStatus);

const reloadPrices = async () => {
  const res = await nativeStoreKitService.getProducts({ productIds: nativeStoreKitService.productIds.all });
  if (res.products?.length > 0) {
    btns.value = res.products.map(p => ({
      title: p.title,
      subtitle: [p.currencySymbol, p.price].join(''),
      click: () => clickProduct(p),
    }))
  }else{
    // alert(`Failed to load subscription prices. Please try again. ${res.error}`);
    btns.value = isPromo ? [] : [{
      title: 'Try Again',
      subtitle: '',
      click: () => reloadPrices(),
    }];
  }
};

async function attempt(func:() => Promise<void>) {
  try {
    isPurchasing.value = true;
    await func();
  } finally {
    isPurchasing.value = false;
  }
  await check();
}

const statusTextError = ref<string>('');
const statusText = computed<string>(() => {
  if (statusTextError.value) return statusTextError.value;

  const status = receiptStatusRef.value;
  if (status) {
    if (status?.expiresOnMs) {
      return `Expired: ${new Date(status.expiresOnMs).toLocaleDateString()}`;
    }else{
      return ``;
    }
  }
  return 'Unknown';
});

async function check():Promise<boolean> {
  try {
    console.log('renewalButtons.check.start...');
    const newStatus = await clientApi.validateReceipt({
      receipt: nativeService.context.receipt,
    });
    receiptStatusRef.value = newStatus?.status;

    const status = receiptStatusRef.value;
    console.log('renewalButtons.check.status: ', JSON.stringify(status));
    if (status?.ok) {
      emits('done', nativeService.context.receipt);
    }
    return status.ok;
  } catch (error) {
    const msg = String(error?.message || error);
    alert(msg);
    statusTextError.value = `Error: ${msg.substring(0, 40).trim()}...`;
    return false;
  }
}

async function clickProduct(p: ISkProduct) {
  await attempt(async () => {
    await nativeStoreKitService.purchase({
      productId: p.identifier,
    });
  });
}

const clickRefresh = async () => {
  await attempt(async () => {
    await nativeStoreKitService.refreshReceipt({});
  });
};


onMounted(async () => {
  const ok = await check();
  if (ok) return;
  await reloadPrices();
});

const clickPromo = async () => {
  clientApi.hit('promo');
  await nativeStoreKitService.goat(Date.now() + (1000 * 60 * 60 * 24* 182));
  await setTimeoutAsync(3000);
  await nativeReviewPromptService.request();
};

const clickTermsOfUse = () => {
  console.log('clickTermsOfUse');
  nativeService.run('open-url', {
    url: 'http://www.apple.com/legal/itunes/appstore/dev/stdeula',
  });
};

const clickPrivacyPolicy = () => {
  console.log('clickPrivacyPolicy');
  nativeService.run('open-url', {
    url: 'https://allstarapps.com/privacy-policy',
  });
};

</script>
